import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Bell,
  Mail,
  Monitor,
  Power,
  Plus,
  X,
  Loader2,
  ShieldAlert,
  Phone,
  TrendingUp,
  LifeBuoy,
  MessageSquare,
} from 'lucide-react';

const EVENT_ICONS = {
  new_eqf_application: Bell,
  call_logged: Phone,
  deal_status_change: TrendingUp,
  ticket_created: LifeBuoy,
  ticket_reply: MessageSquare,
};

const DELIVERY_OPTIONS = [
  { value: 'email', label: 'Email Only', icon: Mail },
  { value: 'in_app', label: 'In-App Only', icon: Monitor },
  { value: 'both', label: 'Both', icon: Bell },
];

const RECIPIENT_OPTIONS = [
  { value: 'ptc_admin', label: 'PTC Admin' },
  { value: 'company_director', label: 'Company Director' },
  { value: 'assigned_user', label: 'Assigned User' },
];

export default function NotificationsPage() {
  const { isAdmin } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [customEmailInputs, setCustomEmailInputs] = useState({});

  const fetchSettings = useCallback(async () => {
    try {
      const res = await api.get('/notifications/settings');
      setSettings(res.data);
    } catch (err) {
      toast.error('Failed to load notification settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const toggleGlobal = async (enabled) => {
    setSaving(s => ({ ...s, global: true }));
    try {
      await api.put('/notifications/settings/global-toggle', { emails_enabled: enabled });
      setSettings(s => ({ ...s, emails_enabled: enabled }));
      toast.success(enabled ? 'Emails enabled' : 'All emails disabled');
    } catch (err) {
      toast.error('Failed to update');
    } finally {
      setSaving(s => ({ ...s, global: false }));
    }
  };

  const updateEvent = async (eventKey, updates) => {
    setSaving(s => ({ ...s, [eventKey]: true }));
    try {
      await api.put('/notifications/settings/event', { event_key: eventKey, ...updates });
      setSettings(s => ({
        ...s,
        events: s.events.map(ev =>
          ev.event_key === eventKey ? { ...ev, ...updates } : ev
        ),
      }));
      toast.success('Setting updated');
    } catch (err) {
      toast.error('Failed to update event setting');
    } finally {
      setSaving(s => ({ ...s, [eventKey]: false }));
    }
  };

  const toggleRecipient = (eventKey, recipientValue, currentRecipients) => {
    const newRecipients = currentRecipients.includes(recipientValue)
      ? currentRecipients.filter(r => r !== recipientValue)
      : [...currentRecipients, recipientValue];
    updateEvent(eventKey, { recipients: newRecipients });
  };

  const addCustomEmail = (eventKey, currentCustom) => {
    const email = (customEmailInputs[eventKey] || '').trim().toLowerCase();
    if (!email || !email.includes('@')) {
      toast.error('Enter a valid email');
      return;
    }
    if (currentCustom.includes(email)) {
      toast.error('Email already added');
      return;
    }
    updateEvent(eventKey, { custom_emails: [...currentCustom, email] });
    setCustomEmailInputs(s => ({ ...s, [eventKey]: '' }));
  };

  const removeCustomEmail = (eventKey, email, currentCustom) => {
    updateEvent(eventKey, { custom_emails: currentCustom.filter(e => e !== email) });
  };

  if (!isAdmin()) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-slate-500">Admin access required</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  const emailsEnabled = settings?.emails_enabled !== false;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="notifications-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-primary">Notification Settings</h1>
        <p className="text-sm text-slate-500 mt-1">Control which events trigger emails and in-app notifications</p>
      </div>

      {/* Global Kill Switch */}
      <Card className={`border-2 ${emailsEnabled ? 'border-emerald-200 bg-emerald-50/30' : 'border-red-200 bg-red-50/30'}`} data-testid="global-email-toggle-card">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${emailsEnabled ? 'bg-emerald-100' : 'bg-red-100'}`}>
                <Power className={`h-5 w-5 ${emailsEnabled ? 'text-emerald-600' : 'text-red-600'}`} />
              </div>
              <div>
                <p className="font-semibold text-slate-800">Global Email Sending</p>
                <p className="text-xs text-slate-500">
                  {emailsEnabled
                    ? 'Emails are active — events will send based on individual settings below'
                    : 'All outgoing emails are DISABLED — no emails will be sent for any event'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Badge className={emailsEnabled ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                {emailsEnabled ? 'ON' : 'OFF'}
              </Badge>
              <Switch
                checked={emailsEnabled}
                onCheckedChange={toggleGlobal}
                disabled={saving.global}
                data-testid="global-email-switch"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Event Settings */}
      <div className="space-y-3">
        <h2 className="text-sm font-semibold text-slate-600 uppercase tracking-wider">Event Configuration</h2>
        {(settings?.events || []).map((ev) => {
          const Icon = EVENT_ICONS[ev.event_key] || Bell;
          const isSaving = saving[ev.event_key];
          return (
            <Card
              key={ev.event_key}
              className={`transition-opacity ${!emailsEnabled ? 'opacity-50' : ''}`}
              data-testid={`event-card-${ev.event_key}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  {/* Event info + enable toggle */}
                  <div className="flex items-center gap-3 min-w-[220px]">
                    <div className={`p-2 rounded-lg ${ev.enabled ? 'bg-blue-100' : 'bg-slate-100'}`}>
                      <Icon className={`h-4 w-4 ${ev.enabled ? 'text-blue-600' : 'text-slate-400'}`} />
                    </div>
                    <div>
                      <p className={`text-sm font-medium ${ev.enabled ? 'text-slate-800' : 'text-slate-400'}`}>
                        {ev.label}
                      </p>
                      <p className="text-[10px] text-slate-400">{ev.event_key}</p>
                    </div>
                  </div>

                  {/* Enable/Disable */}
                  <div className="flex flex-col items-center gap-1">
                    <span className="text-[10px] text-slate-400 uppercase">Enabled</span>
                    <Switch
                      checked={ev.enabled}
                      onCheckedChange={(val) => updateEvent(ev.event_key, { enabled: val })}
                      disabled={isSaving}
                      data-testid={`event-enabled-${ev.event_key}`}
                    />
                  </div>

                  {/* Delivery Type */}
                  <div className="flex flex-col gap-1 min-w-[140px]">
                    <span className="text-[10px] text-slate-400 uppercase">Delivery</span>
                    <Select
                      value={ev.delivery || 'both'}
                      onValueChange={(val) => updateEvent(ev.event_key, { delivery: val })}
                      disabled={!ev.enabled || isSaving}
                    >
                      <SelectTrigger className="h-8 text-xs" data-testid={`event-delivery-${ev.event_key}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DELIVERY_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value} className="text-xs">
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Recipients */}
                  <div className="flex-1">
                    <span className="text-[10px] text-slate-400 uppercase">Recipients</span>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {RECIPIENT_OPTIONS.map(opt => {
                        const isActive = (ev.recipients || []).includes(opt.value);
                        return (
                          <button
                            key={opt.value}
                            onClick={() => toggleRecipient(ev.event_key, opt.value, ev.recipients || [])}
                            disabled={!ev.enabled || isSaving}
                            className={`px-2 py-1 text-[11px] rounded-full border transition-colors ${
                              isActive
                                ? 'bg-blue-100 border-blue-300 text-blue-700 font-medium'
                                : 'bg-white border-slate-200 text-slate-400 hover:border-slate-300'
                            } ${!ev.enabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                            data-testid={`recipient-${ev.event_key}-${opt.value}`}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                    {/* Custom emails */}
                    {ev.enabled && (
                      <div className="mt-2">
                        <div className="flex flex-wrap gap-1 mb-1">
                          {(ev.custom_emails || []).map(email => (
                            <span key={email} className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 border border-amber-200 rounded-full text-[10px] text-amber-700">
                              {email}
                              <button onClick={() => removeCustomEmail(ev.event_key, email, ev.custom_emails || [])} className="hover:text-red-600">
                                <X className="h-3 w-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                        <div className="flex items-center gap-1">
                          <Input
                            value={customEmailInputs[ev.event_key] || ''}
                            onChange={(e) => setCustomEmailInputs(s => ({ ...s, [ev.event_key]: e.target.value }))}
                            placeholder="Add custom email..."
                            className="h-7 text-xs flex-1"
                            onKeyDown={(e) => { if (e.key === 'Enter') addCustomEmail(ev.event_key, ev.custom_emails || []); }}
                            data-testid={`custom-email-input-${ev.event_key}`}
                          />
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2"
                            onClick={() => addCustomEmail(ev.event_key, ev.custom_emails || [])}
                            data-testid={`add-custom-email-${ev.event_key}`}
                          >
                            <Plus className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Info footer */}
      <div className="flex items-start gap-2 p-3 bg-slate-50 border border-slate-200 rounded-lg">
        <ShieldAlert className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
        <p className="text-xs text-slate-500">
          Changes take effect immediately. When emails are disabled globally, no outgoing emails
          will be sent for any event. In-app notifications are not affected by the global email toggle.
        </p>
      </div>
    </div>
  );
}
